import React from 'react';

/**
 * Radar/Kiviat chart component for framework compliance by domain
 * @param {Object} props
 * @param {Object} props.data - Object with domain names as keys and scores (0-100) as values
 * @param {number} props.size - Size of the chart in pixels (default: 300)
 */
export default function RadarChart({ data = {}, size = 300 }) {
  const center = size / 2;
  const radius = size * 0.35; // Chart radius (leaving padding)

  // Derive domains from data keys (framework-agnostic)
  const functions = Object.keys(data).filter(k => typeof data[k] === 'number' || typeof data[k] === 'object');
  const numAxes = functions.length;
  const angleStep = (2 * Math.PI) / numAxes;
  
  // Calculate points for each axis
  const axes = functions.map((func, index) => {
    const angle = (index * angleStep) - (Math.PI / 2); // Start from top
    const x = center + radius * Math.cos(angle);
    const y = center + radius * Math.sin(angle);
    return { func, angle, x, y, index };
  });
  
  // Get scores for each function (default to 0 if not provided)
  const scores = functions.map(func => data[func] || 0);
  
  // Calculate data points (scale score 0-100 to 0-radius)
  const dataPoints = axes.map((axis, index) => {
    const scoreRadius = (scores[index] / 100) * radius;
    const x = center + scoreRadius * Math.cos(axis.angle);
    const y = center + scoreRadius * Math.sin(axis.angle);
    return { x, y };
  });
  
  // Create path for the filled area
  const pathData = dataPoints.map((point, index) => {
    return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
  }).join(' ') + ' Z';
  
  // Color based on average score
  const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
  const getColor = (score) => {
    if (score >= 80) return '#10b981'; // Green
    if (score >= 50) return '#f59e0b'; // Amber
    return '#ef4444'; // Red
  };
  const fillColor = getColor(avgScore);
  
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
        {/* Grid circles */}
        {[0.25, 0.5, 0.75, 1.0].map((scale, i) => (
          <circle
            key={i}
            cx={center}
            cy={center}
            r={radius * scale}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth="1"
            strokeDasharray={i === 3 ? "none" : "2,2"}
          />
        ))}
        
        {/* Axes lines */}
        {axes.map((axis, index) => (
          <line
            key={index}
            x1={center}
            y1={center}
            x2={axis.x}
            y2={axis.y}
            stroke="#cbd5e1"
            strokeWidth="1"
          />
        ))}
        
        {/* Data area (filled polygon) */}
        <path
          d={pathData}
          fill={fillColor}
          fillOpacity="0.2"
          stroke={fillColor}
          strokeWidth="2"
        />
        
        {/* Data points */}
        {dataPoints.map((point, index) => (
          <circle
            key={index}
            cx={point.x}
            cy={point.y}
            r="4"
            fill={getColor(scores[index])}
            stroke="white"
            strokeWidth="2"
          />
        ))}
        
        {/* Function labels */}
        {axes.map((axis, index) => {
          const labelRadius = radius * 1.15; // Slightly outside the chart
          const labelX = center + labelRadius * Math.cos(axis.angle);
          const labelY = center + labelRadius * Math.sin(axis.angle);
          const score = scores[index];
          
          return (
            <g key={index}>
              <text
                x={labelX}
                y={labelY}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-xs font-semibold fill-slate-700"
              >
                {axis.func}
              </text>
              <text
                x={labelX}
                y={labelY + 14}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-xs font-bold"
                fill={getColor(score)}
              >
                {score}%
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
